import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useRef,
} from "react";
import moment from "moment";
import {
  approveConferenceRegistrationApiCall,
  createConferenceApiCall,
  deleteConferenceApiCall,
  denyConferenceRegistrationApiCall,
  getAllConferencesApiCall,
  getPublicConferenceDetailsApiCall,
  joinConferenceWithTokenApiCall,
  listConferenceRegistrationsApiCall,
  registerPublicConferenceApiCall,
  updateConferenceApiCall,
  getConferencePollsApiCall,
  createPollQuestionApiCall,
  updatePollQuestionApiCall,
  deletePollQuestionApiCall,
  createPollOptionApiCall,
  updatePollOptionApiCall,
  deletePollOptionApiCall,
  reorderPollQuestionsApiCall,
  reorderPollOptionsApiCall,
  launchPollApiCall,
  endPollApiCall,
  getPollResultsApiCall,
  submitPollResponseApiCall,
  toggleConferenceChatApiCall,
} from "../API/conferences/conferencesService";

export const ConferencesContext = createContext({
  conferences: [],
  currentConference: null,
  conferencesRef: [],
  currentConferenceRef: [],
  createConference: () => {},
  updateConference: () => {},
  getConferences: () => {},
  getPublicConferenceDetails: () => {},
  deleteConference: () => {},
  joinConferenceWithToken: () => {},
  registerPublicConference: () => {},
  listConferenceRegistrations: () => {},
  approveConferenceRegistration: () => {},
  denyConferenceRegistration: () => {},
  setCurrentConference: () => {},
  getConferencePolls: () => {},
  createPollQuestion: () => {},
  updatePollQuestion: () => {},
  deletePollQuestion: () => {},
  createPollOption: () => {},
  updatePollOption: () => {},
  deletePollOption: () => {},
  launchPoll: () => {},
  endPoll: () => {},
  getPollResults: () => {},
  submitPollResponse: () => {},
  toggleConferenceChat: () => {},
});

const ConferencesContextProvider = ({ children }) => {
  const [conferences, setConferences] = useState([]);
  const [currentConference, setCurrentConference] = useState(null);

  const conferencesRef = useRef(null);
  const currentConferenceRef = useRef(null);

  const createConference = useCallback(
    async (dataObj, onSuccess, errorCallback) => {
      try {
        await createConferenceApiCall(
          dataObj,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const updateConference = useCallback(
    async (objData, onSuccess, errorCallback) => {
      try {
        await updateConferenceApiCall(
          objData,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const getConferences = useCallback(async (onSuccess, errorCallback) => {
    const handleResponse = (response) => {
      const sortedConfs = response.data.conferences.sort((a, b) =>
        moment(a.start_at).isAfter(moment(b.start_at)) ? 1 : -1
      );
      conferencesRef.current = sortedConfs;
      setConferences(sortedConfs);
    };

    try {
      await getAllConferencesApiCall(
        (response) => {
          handleResponse(response);
          onSuccess(response);
        },
        (error) => errorCallback(error)
      );
    } catch (err) {
      errorCallback(err);
    }
  }, []);

  const getPublicConferenceDetails = useCallback(
    async (conferenceId, onSuccess, errorCallback) => {
      try {
        await getPublicConferenceDetailsApiCall(
          conferenceId,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const deleteConference = useCallback(
    async (conferenceId, onSuccess, errorCallback) => {
      try {
        await deleteConferenceApiCall(
          conferenceId,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const joinConferenceWithToken = useCallback(
    async (token, linkToken, onSuccess, errorCallback) => {
      try {
        await joinConferenceWithTokenApiCall(
          token,
          linkToken,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const registerPublicConference = useCallback(
    async (
      conferenceId,
      firstName,
      lastName,
      email,
      onSuccess,
      errorCallback
    ) => {
      try {
        await registerPublicConferenceApiCall(
          conferenceId,
          firstName,
          lastName,
          email,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const listConferenceRegistrations = useCallback(
    async (conferenceId, onSuccess, errorCallback) => {
      try {
        await listConferenceRegistrationsApiCall(
          conferenceId,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const approveConferenceRegistration = useCallback(
    async (conferenceId, registrationId, onSuccess, errorCallback) => {
      try {
        await approveConferenceRegistrationApiCall(
          conferenceId,
          registrationId,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const denyConferenceRegistration = useCallback(
    async (conferenceId, registrationId, onSuccess, errorCallback) => {
      try {
        await denyConferenceRegistrationApiCall(
          conferenceId,
          registrationId,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const getConferencePolls = useCallback(
  async (conferenceId, onSuccess, errorCallback) => {
    try {
      await getConferencePollsApiCall(
        conferenceId,
        (response) => onSuccess(response),
        (error) => errorCallback(error)
      );
    } catch (err) {
      errorCallback(err);
    }
  },
  []
  );

  const createPollQuestion = useCallback(
    async (conferenceId, questionText, answerType, position, onSuccess, errorCallback) => {
      try {
        await createPollQuestionApiCall(
          conferenceId,
          questionText,
          answerType,
          position,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const updatePollQuestion = useCallback(
    async (questionId, questionText, answerType, onSuccess, errorCallback) => {
      try {
        await updatePollQuestionApiCall(
          questionId,
          questionText,
          answerType,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const deletePollQuestion = useCallback(
    async (questionId, onSuccess, errorCallback) => {
      try {
        await deletePollQuestionApiCall(
          questionId,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const createPollOption = useCallback(
    async (questionId, optionText, position, onSuccess, errorCallback) => {
      try {
        await createPollOptionApiCall(
          questionId,
          optionText,
          position,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const updatePollOption = useCallback(
    async (optionId, optionText, position, onSuccess, errorCallback) => {
      try {
        await updatePollOptionApiCall(
          optionId,
          optionText,
          position,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const deletePollOption = useCallback(
    async (optionId, onSuccess, errorCallback) => {
      try {
        await deletePollOptionApiCall(
          optionId,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const reorderPollQuestions = useCallback(
    async (conferenceId, positions, onSuccess, errorCallback) => {
      try {
        await reorderPollQuestionsApiCall(
          conferenceId,
          positions,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const reorderPollOptions = useCallback(
    async (questionId, positions, onSuccess, errorCallback) => {
      try {
        await reorderPollOptionsApiCall(
          questionId,
          positions,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const launchPoll = useCallback(
    async (questionId, onSuccess, errorCallback) => {
      try {
        await launchPollApiCall(
          questionId,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const endPoll = useCallback(
    async (questionId, onSuccess, errorCallback) => {
      try {
        await endPollApiCall(
          questionId,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const toggleConferenceChat = useCallback(
    async (conferenceId, allowChat, onSuccess, errorCallback) => {
      try {
        await toggleConferenceChatApiCall(
          conferenceId,
          allowChat,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const getPollResults = useCallback(
    async (questionId, onSuccess, errorCallback) => {
      try {
        await getPollResultsApiCall(
          questionId,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const submitPollResponse = useCallback(
    async (questionId, selectedOptionIds, onSuccess, errorCallback) => {
      try {
        await submitPollResponseApiCall(
          questionId,
          selectedOptionIds,
          (response) => onSuccess(response),
          (error) => errorCallback(error)
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      conferences,
      createConference,
      conferencesRef,
      updateConference,
      getConferences,
      getPublicConferenceDetails,
      deleteConference,
      joinConferenceWithToken,
      registerPublicConference,
      listConferenceRegistrations,
      approveConferenceRegistration,
      denyConferenceRegistration,
      currentConference,
      setCurrentConference,
      currentConferenceRef,
      getConferencePolls,
      createPollQuestion,
      updatePollQuestion,
      deletePollQuestion,
      createPollOption,
      updatePollOption,
      deletePollOption,
      reorderPollQuestions,
      reorderPollOptions,
      launchPoll,
      endPoll,
      getPollResults,
      submitPollResponse,
      toggleConferenceChat,
    }),
    [
      conferences,
      createConference,
      conferencesRef,
      updateConference,
      getConferences,
      getPublicConferenceDetails,
      deleteConference,
      joinConferenceWithToken,
      registerPublicConference,
      listConferenceRegistrations,
      approveConferenceRegistration,
      denyConferenceRegistration,
      currentConference,
      setCurrentConference,
      currentConferenceRef,
      getConferencePolls,
      createPollQuestion,
      updatePollQuestion,
      deletePollQuestion,
      createPollOption,
      updatePollOption,
      deletePollOption,
      reorderPollQuestions,
      reorderPollOptions,
      launchPoll,
      endPoll,
      getPollResults,
      submitPollResponse,
      toggleConferenceChat,
    ]
  );

  return (
    <ConferencesContext.Provider value={contextValue}>
      {children}
    </ConferencesContext.Provider>
  );
};

export const useConferencesService = () => useContext(ConferencesContext);

export default ConferencesContextProvider;
